export interface ConnectivityApi {
  isOnline(): boolean;
  // Whether isOnline() is a measured answer rather than the optimistic default.
  // A boot path that branches on isOnline() alone would take the online branch
  // on a dead network before the first probe has run.
  isKnown(): boolean;
  probe(force?: boolean): Promise<boolean>;
  onChange(callback: (online: boolean) => void): () => void;
  stop(): void;
}

export interface ConnectivityOptions {
  userPoolId?: string;
  region?: string;
  timeoutMs?: number;
  cacheMs?: number;
  pollMs?: number;
}

const DEFAULT_TIMEOUT_MS = 2500;
const DEFAULT_CACHE_MS = 30_000;
const DEFAULT_POLL_MS = 60_000;

function discoveryUrl(region: string, userPoolId: string): string {
  return `https://cognito-idp.${region}.amazonaws.com/${userPoolId}/.well-known/openid-configuration`;
}

// The pool's OIDC discovery document is the cheapest thing that proves Cognito
// itself is reachable, which is the only question that matters: a barge has a
// working LAN and no internet, so navigator.onLine reports true and would
// defeat the whole feature. no-cors makes the answer independent of whatever
// CORS policy that endpoint carries — a rejected preflight and a dead network
// are indistinguishable to fetch, and only one of them means offline.
async function probeOnce(url: string, timeoutMs: number): Promise<boolean> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    await fetch(url, {
      mode: "no-cors",
      cache: "no-store",
      signal: controller.signal,
    });
    return true;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

// A factory, never a module singleton: the offline poll below is a live timer,
// and an instance created at import time would tick in every renderer and
// every test that happens to pull this file into its graph. The caller that
// wants connectivity owns the instance and owns stop().
export function createConnectivityApi(
  options: ConnectivityOptions = {},
): ConnectivityApi {
  const userPoolId =
    options.userPoolId ?? import.meta.env.VITE_AWS_USER_POOL_ID ?? "";
  const region = options.region ?? import.meta.env.VITE_AWS_REGION ?? "";
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const cacheMs = options.cacheMs ?? DEFAULT_CACHE_MS;
  const pollMs = options.pollMs ?? DEFAULT_POLL_MS;

  const url = discoveryUrl(region, userPoolId);
  const configured = Boolean(region && userPoolId);

  const subscribers = new Set<(online: boolean) => void>();
  let online = true;
  let probedAt = 0;
  let hasProbed = false;
  let inFlight: Promise<boolean> | null = null;
  let pollTimer: ReturnType<typeof setInterval> | null = null;
  let stopped = false;
  let warned = false;

  // The poll asks the network, never the cache. Caching a result and polling
  // for liveness are different jobs, and routing the poll through the cache
  // would make the real reconnect-detection interval max(pollMs, cacheMs) —
  // silently, and only for whoever tunes the numbers.
  function startPolling(): void {
    if (stopped || pollTimer) return;
    pollTimer = setInterval(() => void runProbe(true), pollMs);
  }

  function stopPolling(): void {
    if (!pollTimer) return;
    clearInterval(pollTimer);
    pollTimer = null;
  }

  function setOnline(next: boolean): void {
    if (next === online) return;
    online = next;
    if (online) stopPolling();
    else startPolling();
    subscribers.forEach((callback) => callback(online));
  }

  // Missing Cognito config means Amplify cannot sign anyone in either, so the
  // probe has nothing to report on. Reporting online keeps today's behaviour
  // rather than stranding a misconfigured build in offline mode.
  function reportUnconfigured(): boolean {
    if (!warned) {
      warned = true;
      console.warn(
        "[connectivity] VITE_AWS_USER_POOL_ID or VITE_AWS_REGION is unset; reporting online without probing",
      );
    }
    setOnline(true);
    return true;
  }

  function runProbe(force: boolean): Promise<boolean> {
    if (!configured) return Promise.resolve(reportUnconfigured());
    if (!force && hasProbed && Date.now() - probedAt < cacheMs) {
      return Promise.resolve(online);
    }
    if (inFlight) return inFlight;

    inFlight = probeOnce(url, timeoutMs)
      .then((reachable) => {
        probedAt = Date.now();
        hasProbed = true;
        setOnline(reachable);
        return reachable;
      })
      .finally(() => {
        inFlight = null;
      });
    return inFlight;
  }

  return {
    isOnline: () => online,
    isKnown: () => hasProbed,
    probe: (force = false) => runProbe(force),
    onChange(callback) {
      subscribers.add(callback);
      return () => {
        subscribers.delete(callback);
      };
    },
    stop() {
      stopped = true;
      stopPolling();
      subscribers.clear();
    },
  };
}
